import { ResourceHelperService } from '../resource-helper.service';
import { LinkBuilder } from '../link-builder.service';

/**
 * A dataservice that provides CRUD operations on transport operator entries
 * against the backend transport operators service
 */
export class TransportOperatorService {
  private resource: InterACT.Interfaces.IResource<InterACT.Interfaces.TransportOperators.TransportOperatorResponse | InterACT.Interfaces.TransportOperators.TransportOperatorsResponse>;
  private summaryResource: InterACT.Interfaces.IResource<InterACT.Interfaces.TransportOperators.SummaryResponse>;

  constructor(
    private $resource: ng.resource.IResourceService,
    private resourceHelperService: ResourceHelperService,
    private linkBuilder: LinkBuilder
  ) {
    this.resource = $resource('api/transport-operators/:operatorId', undefined, { update: { method: 'PUT' } });
    this.summaryResource = $resource('api/transport-operators/summary');
  }

  /**
   * A method that calls the summary service with the supplied searchText and returns the results
   *
   * @param searchText The search text used to build up a query string in the outgoing http request
   * @returns A promise that resolves with a summary of all the transport operators
   */
  public getSummary(searchText: string) {
    return this.summaryResource.get({ q: searchText }).$promise;
  }

  /**
   * Function to retrieve a record from the Transport Operator backend service
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The search text used to build up a query string in the outgoing http request
   * @returns A http promise
   */
  public getList(page: number, perPageCount: number, searchText?: string) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText);
    return <ng.IPromise<InterACT.Interfaces.TransportOperators.TransportOperatorsResponse>>this.resource.get(options).$promise;
  }
  /**
   * Function to retrieve a record from the Transport Operators backend service
   *
   * @param operatorId The unique Id of the operator to be retrieved
   * @returns An object representation of the retrieved Transport Operator record
   */
  public getOperator(operatorId: string) {
    return this.resource.get({ operatorId: operatorId }).$promise.then((resource) => {
      return (<InterACT.Interfaces.TransportOperators.TransportOperatorResponse>resource).transportOperator;
    });
  }

  /**
   * Function to retrieve a record from the Transport Operators backend service
   *
   * @param operator An object representation of the operator resource to be updated
   * @param reason The reason for the update to the operator provided by the user
   * @returns The promise of the original server interaction
   */
  public update(operator: InterACT.Interfaces.TransportOperators.TransportOperator, reason?: string) {
    if (reason) {
      (<any>operator).customHeaders = {
        auditReason: reason
      };
    }
    return (<any>this.resource).update({ operatorId: operator.id }, operator).$promise;
  }

  /**
   * Function to create a Transport Operator
   *
   * @param operator An object representation of the operator to be created
   * @returns The promise of the original server interaction
   */
  public create(operator: InterACT.Interfaces.TransportOperators.TransportOperator) {
    return this.resource.save(operator).$promise;
  }

  /**
   * Converts and TransportOperator to and OperatorLink
   *
   * @param operator the transport operator
   * @returns the operator link
   */
  public convertOperatorToLink(
    operator: InterACT.Interfaces.TransportOperators.TransportOperator
  ): InterACT.Interfaces.TransportOperatorServices.OperatorLink {
    return <any>{
      link: this.linkBuilder.getTransportOperatorsLink(operator.id),
      name: operator.name
    };
  }
}
